import { c as _c } from "react/compiler-runtime";
// Third Party
import React, { ComponentType, forwardRef } from 'react';
import type { CustomArrowProps, LazyLoadTypes, Settings } from 'react-slick';
import Slider from 'react-slick';

// UI
import { Icon, IconNames } from '@components/icon';

/** Ref handle for programmatic control (e.g. slickGoTo). */
export interface CarouselRef {
  slickGoTo: (index: number, dontAnimate?: boolean) => void;
}
interface CarouselProps {
  children: React.ReactNode;
  showDots?: boolean;
  showArrows?: boolean;
  className?: string;
  nextArrow?: React.ComponentType<CustomArrowProps>;
  prevArrow?: React.ComponentType<CustomArrowProps>;
  responsive?: Settings['responsive'];
  onSlideChange?: (index: number) => void;
  loadingStrategy?: LazyLoadTypes;
  useAspectRatio?: boolean;
  disableSwipe?: boolean;
}
const NextArrow = props => {
  const $ = _c(3);
  const {
    onClick
  } = props;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Icon name={IconNames.CHEVRON_RIGHT} className="h-4 w-4" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== onClick) {
    t1 = <button onClick={onClick} className="absolute right-2 top-1/2 z-10 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 p-2 hover:bg-white">{t0}</button>;
    $[1] = onClick;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const PrevArrow = props => {
  const $ = _c(3);
  const {
    onClick
  } = props;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Icon name={IconNames.CHEVRON_LEFT} className="h-4 w-4" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== onClick) {
    t1 = <button onClick={onClick} className="absolute left-2 top-1/2 z-10 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 p-2 hover:bg-white">{t0}</button>;
    $[1] = onClick;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};

// Cast Slider to ComponentType to resolve React JSX type conflicts
const SliderComponent = Slider as ComponentType<Settings>;
export const Carousel = forwardRef<CarouselRef, CarouselProps>(function Carousel(t0, ref) {
  const $ = _c(24);
  const {
    children,
    showDots: t1,
    showArrows: t2,
    className,
    nextArrow,
    prevArrow,
    responsive,
    onSlideChange,
    loadingStrategy: t3,
    useAspectRatio: t4,
    disableSwipe: t5
  } = t0;
  const showDots = t1 === undefined ? true : t1;
  const showArrows = t2 === undefined ? true : t2;
  const loadingStrategy = t3 === undefined ? "ondemand" : t3;
  const useAspectRatio = t4 === undefined ? true : t4;
  const disableSwipe = t5 === undefined ? false : t5;
  let t6;
  if ($[0] !== nextArrow) {
    t6 = nextArrow ? React.createElement(nextArrow) : <NextArrow />;
    $[0] = nextArrow;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  let t7;
  if ($[2] !== prevArrow) {
    t7 = prevArrow ? React.createElement(prevArrow) : <PrevArrow />;
    $[2] = prevArrow;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const t8 = !disableSwipe;
  const t9 = !disableSwipe;
  const t10 = !disableSwipe;
  let t11;
  if ($[4] !== onSlideChange) {
    t11 = (_oldIndex, newIndex) => {
      onSlideChange?.(newIndex);
    };
    $[4] = onSlideChange;
    $[5] = t11;
  } else {
    t11 = $[5];
  }
  let t12;
  if ($[6] !== loadingStrategy || $[7] !== responsive || $[8] !== showArrows || $[9] !== showDots || $[10] !== t10 || $[11] !== t11 || $[12] !== t6 || $[13] !== t7 || $[14] !== t8 || $[15] !== t9) {
    t12 = {
      dots: showDots,
      infinite: true,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
      arrows: showArrows,
      nextArrow: t6,
      prevArrow: t7,
      adaptiveHeight: false,
      lazyLoad: loadingStrategy,
      dotsClass: "slick-dots custom-dots",
      responsive,
      swipe: t8,
      touchMove: t9,
      draggable: t10,
      beforeChange: t11
    };
    $[6] = loadingStrategy;
    $[7] = responsive;
    $[8] = showArrows;
    $[9] = showDots;
    $[10] = t10;
    $[11] = t11;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  const settings = t12;
  const t13 = `carousel-wrapper ${useAspectRatio ? "aspect-ratio" : ""} ${disableSwipe ? "swipe-disabled" : ""} ${className || ""}`;
  let t14;
  if ($[17] !== children || $[18] !== ref || $[19] !== settings) {
    t14 = <SliderComponent ref={ref} {...settings}>{children}</SliderComponent>;
    $[17] = children;
    $[18] = ref;
    $[19] = settings;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== t13 || $[22] !== t14) {
    t15 = <div className={t13}>{t14}</div>;
    $[21] = t13;
    $[22] = t14;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  return t15;
});
Carousel.displayName = 'Carousel';